// src/components/HeroSection.js
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FaArrowDown } from "react-icons/fa";
import { Link } from "react-scroll";
import heroBg from "../assets/hero-bg.webp";

const Hero = styled.section`
  height: 100vh;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding: 0;
  id: "home";
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url(${heroBg}) no-repeat center center/cover;
  z-index: -2;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to right, rgba(0,0,0,0.9), rgba(0,0,0,0.3));
  z-index: -1;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 100%;

  @media (max-width: 768px) {
    justify-content: center;
    text-align: center;
  }
`;

const Content = styled(motion.div)`
  max-width: 600px;
  z-index: 1;
  margin-top: 50px;

  @media (max-width: 768px) {
    margin-top: 80px;
  }
`;

const Title = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 20px;
  color: #fff;
  text-shadow: 0 0 10px #ff9900, 0 0 20px #ff6600;

  span {
    display: block;
    color: #ff9900;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: clamp(1rem, 1.5vw, 1.2rem);
  margin-bottom: 30px;
  color: #fff;
  text-shadow: 0 0 8px rgba(255, 255, 255, 0.5);
  max-width: 500px;
  line-height: 1.6;
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  gap: 20px;

  @media (max-width: 768px) {
    justify-content: center;
    flex-wrap: wrap;
  }
`;

const PrimaryButton = styled(motion.button)`
  background: linear-gradient(45deg, #ff9900, #ff6600);
  padding: 15px 30px;
  border: none;
  border-radius: 50px;
  color: #000;
  font-weight: bold;
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 2px;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(255, 153, 0, 0.5);
  transition: all 0.3s ease;
  font-family: 'Orbitron', sans-serif;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(255, 153, 0, 0.7);
  }
`;



const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #fff;
  cursor: pointer;

  span {
    font-size: 14px;
    margin-bottom: 10px;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 1px;
  }
`;

const HeroSection = () => {
  const [offsetY, setOffsetY] = useState(0);

  const handleScroll = () => setOffsetY(window.pageYOffset);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Hero id="home">
      <HeroBackground style={{ transform: `translateY(${offsetY * 0.5}px)` }} />
      <Overlay />
      <ContentContainer>
        <Content
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Title>
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              Unleash The Fun
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              At Hopito Gaming Zone
            </motion.span>
          </Title>

          <Subtitle
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            Experience the ultimate gaming adventure with trampolines, VR games, bowling, and more. Get ready for the adrenaline rush!
          </Subtitle>

          <ButtonGroup
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
          >
            <PrimaryButton whileTap={{ scale: 0.95 }}>
              OPEN
            </PrimaryButton>
            {/* <PrimaryButton whileTap={{ scale: 0.95 }}>
              Book Now
            </PrimaryButton>
            <SecondaryButton whileTap={{ scale: 0.95 }}>
              Our Activities
            </SecondaryButton> */}
          </ButtonGroup>
        </Content>
      </ContentContainer>

      <Link to="activities" smooth={true} duration={800}>
        <ScrollIndicator
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            y: [0, 10, 0],
          }}
          transition={{
            delay: 1.5,
            duration: 1.5,
            repeat: Infinity,
            repeatType: "loop"
          }}
        >
          <span>Scroll Down</span>
          <FaArrowDown />
        </ScrollIndicator>
      </Link>
    </Hero>
  );
};

export default HeroSection;